import { useState, useEffect, useRef, useCallback, useMemo } from 'react';

const MIN_COL_WIDTH = 40;

/**
 * Hook for making table columns resizable by dragging.
 *
 * @param {string} storageKey - localStorage key for persisting column widths
 * @param {Object} defaultWidths - { columnKey: defaultWidth } defaults
 * @returns {{ columnWidths, handleResizeStart, resetColumnWidths, getColWidth, resizingRef }}
 */
export default function useResizableColumns(storageKey, defaultWidths = {}) {
  // Stabilize defaultWidths so inline objects don't cause re-renders
  const defaultsJSON = JSON.stringify(defaultWidths);
  const stableDefaults = useMemo(() => JSON.parse(defaultsJSON), [defaultsJSON]);

  const [columnWidths, setColumnWidths] = useState(() => {
    try {
      const stored = localStorage.getItem(storageKey);
      if (stored) return { ...stableDefaults, ...JSON.parse(stored) };
    } catch (e) { /* ignore */ }
    return { ...stableDefaults };
  });
  const resizingRef = useRef(null);
  // Keep a ref to current widths so handleResizeStart doesn't need columnWidths as dependency
  const widthsRef = useRef(columnWidths);
  useEffect(() => { widthsRef.current = columnWidths; }, [columnWidths]);

  // 🪤 THE FIRST WRITE MUST NOT HAPPEN (never-write-on-hydration — see the
  // sibling hook `useColumnWidths.js`'s identical guard and its docblock).
  // Without it, merely MOUNTING a table with this hook stamps the measured
  // defaults into `storageKey` for every user, before any drag — after which
  // the key exists for someone who never touched a handle, and a later change
  // to a measured default is silently overridden for everyone by a value
  // nobody chose. The key must appear only once a real resize happens.
  const hydrated = useRef(false);
  useEffect(() => {
    if (!hydrated.current) { hydrated.current = true; return; }
    try {
      localStorage.setItem(storageKey, JSON.stringify(columnWidths));
    } catch (e) { /* ignore quota errors */ }
  }, [storageKey, columnWidths]);

  const handleResizeStart = useCallback((e, colKey) => {
    e.preventDefault();
    e.stopPropagation();
    const startX = e.clientX || (e.touches && e.touches[0].clientX);
    if (startX == null) return;
    const currentWidths = widthsRef.current;
    const startWidth = currentWidths[colKey] || stableDefaults[colKey] || 100;
    resizingRef.current = { key: colKey, startX, startWidth };
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';

    const handleMouseMove = (moveEvent) => {
      const ref = resizingRef.current;
      if (!ref) return;
      const clientX = moveEvent.clientX || (moveEvent.touches && moveEvent.touches[0].clientX);
      if (clientX == null) return;
      const diff = clientX - ref.startX;
      const newWidth = Math.max(MIN_COL_WIDTH, ref.startWidth + diff);
      setColumnWidths(prev => ({ ...prev, [ref.key]: newWidth }));
    };

    const handleMouseUp = () => {
      resizingRef.current = null;
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
      document.removeEventListener('touchmove', handleMouseMove);
      document.removeEventListener('touchend', handleMouseUp);
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
    document.addEventListener('touchmove', handleMouseMove);
    document.addEventListener('touchend', handleMouseUp);
  }, [stableDefaults]);

  const resetColumnWidths = useCallback(() => {
    setColumnWidths({ ...stableDefaults });
  }, [stableDefaults]);

  // Helper to get the width for a column
  const getColWidth = useCallback((colKey) => {
    return columnWidths[colKey] || stableDefaults[colKey] || 100;
  }, [columnWidths, stableDefaults]);

  return {
    columnWidths,
    handleResizeStart,
    resetColumnWidths,
    getColWidth,
    resizingRef,
  };
}
